/**
 * Instant search dropdown under the header field.
 */

const DEBOUNCE_MS = 280;
const SEARCH_ENDPOINT = 'done-commerce/v1/search';
const MIN_QUERY_LENGTH = 2;

function debounce(fn, wait) {
	let timer;
	return (...args) => {
		clearTimeout(timer);
		timer = setTimeout(() => fn(...args), wait);
	};
}

function getConfig() {
	return window.doneCommerce || {};
}

function escapeHtml(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function renderHighlighted(value) {
	return String(value ?? '').replace(/<(?!\/?mark>)[^>]*>/gi, '');
}

function stockLabel(status, config) {
	const labels = config.i18n?.stockStatus || {};
	if (status === 'instock') {
		return labels.instock || 'In stock';
	}
	if (status === 'onbackorder') {
		return labels.onbackorder || 'Available on backorder';
	}
	if (status === 'outofstock') {
		return labels.outofstock || 'Out of stock';
	}
	return '';
}

function renderSkeletons(count = 5) {
	return Array.from({ length: count }, () => `
		<div class="dc-search-result dc-search-result--skeleton" aria-hidden="true">
			<span class="dc-search-result__thumb"></span>
			<span class="dc-search-result__meta">
				<span class="dc-search-result__title"></span>
				<span class="dc-search-result__sub"></span>
			</span>
		</div>
	`).join('');
}

function renderSuggestionGroup(title, items, config) {
	if (!items?.length) {
		return '';
	}

	const rows = items
		.map((term) => {
			const href = buildSearchFallbackUrl(term, config);
			return `
				<a href="${href}" class="dc-search-suggestion" data-search-suggestion>
					<span class="dc-search-suggestion__term">${escapeHtml(term)}</span>
				</a>
			`;
		})
		.join('');

	return `
		<div class="dc-search-group">
			<h3 class="dc-search-group__title">${escapeHtml(title)}</h3>
			<div class="dc-search-suggestions">${rows}</div>
		</div>
	`;
}

function renderSuggestions(data, config) {
	const recent = renderSuggestionGroup(
		config.i18n?.searchRecent || 'Recent searches',
		data.recent || [],
		config
	);
	const popular = renderSuggestionGroup(
		config.i18n?.searchPopular || 'Popular searches',
		data.popular || [],
		config
	);

	if (!recent && !popular) {
		return `<p class="dc-search-empty">${escapeHtml(config.i18n?.searchStart || 'Search products, brands, or health goals.')}</p>`;
	}

	return `${recent}${popular}`;
}

function buildSearchFallbackUrl(query, config) {
	const shopUrl = config.shopUrl || config.homeUrl || '/';
	if (!query) {
		return shopUrl;
	}
	const separator = shopUrl.includes('?') ? '&' : '?';
	return `${shopUrl}${separator}s=${encodeURIComponent(query)}`;
}

function renderFallback(query, config) {
	const href = buildSearchFallbackUrl(query, config);

	return `
		<p class="dc-search-fallback">
			${escapeHtml(config.i18n?.searchFallbackIntro || 'Instant search is unavailable.')}
			<a href="${href}">${escapeHtml(config.i18n?.searchFallbackLink || 'View all search results')}</a>
		</p>
	`;
}

function renderResults(data, focusedIndex, config) {
	const groups = [];
	let index = 0;

	const productItems = (data.products || []).map((item) => {
		const current = index++;
		const focused = current === focusedIndex ? ' is-focused' : '';
		const thumb = item.thumbnail
			? `<img class="dc-search-result__thumb" src="${escapeHtml(item.thumbnail)}" alt="" loading="lazy">`
			: '<span class="dc-search-result__thumb" aria-hidden="true"></span>';
		const stock = stockLabel(item.stock_status, config);
		const stockClass = item.stock_status ? ` dc-search-result__stock--${escapeHtml(item.stock_status)}` : '';
		const price = item.price_html || item.price
			? `<span class="dc-search-result__price">${item.price_html || escapeHtml(item.price)}</span>`
			: '';
		const stockHtml = stock
			? `<span class="dc-search-result__stock${stockClass}">${escapeHtml(stock)}</span>`
			: '';
		const footer = stockHtml || price
			? `<span class="dc-search-result__footer">${stockHtml}${price}</span>`
			: '';

		return `
			<a href="${escapeHtml(item.url)}" class="dc-search-result${focused}" role="option" id="dc-search-option-${current}" data-index="${current}">
				${thumb}
				<span class="dc-search-result__meta">
					<span class="dc-search-result__title">${renderHighlighted(item.label || item.title || '')}</span>
					${item.brand ? `<span class="dc-search-result__sub">${escapeHtml(item.brand)}</span>` : ''}
					${footer}
				</span>
			</a>
		`;
	});

	if (productItems.length) {
		groups.push(`
			<div class="dc-search-group">
				<h3 class="dc-search-group__title">${escapeHtml(config.i18n?.searchProducts || 'Products')}</h3>
				${productItems.join('')}
			</div>
		`);
	}

	const brandItems = (data.brands || []).map((item) => {
		const current = index++;
		const focused = current === focusedIndex ? ' is-focused' : '';
		return `
			<a href="${escapeHtml(item.url)}" class="dc-search-result${focused}" role="option" id="dc-search-option-${current}" data-index="${current}">
				<span class="dc-search-result__meta">
					<span class="dc-search-result__title">${renderHighlighted(item.label || item.name || '')}</span>
					<span class="dc-search-result__sub">${escapeHtml(config.i18n?.searchBrand || 'Brand')}</span>
				</span>
			</a>
		`;
	});

	if (brandItems.length) {
		groups.push(`
			<div class="dc-search-group">
				<h3 class="dc-search-group__title">${escapeHtml(config.i18n?.searchBrands || 'Brands')}</h3>
				${brandItems.join('')}
			</div>
		`);
	}

	const goalItems = (data.health_goals || []).map((item) => {
		const current = index++;
		const focused = current === focusedIndex ? ' is-focused' : '';
		return `
			<a href="${escapeHtml(item.url)}" class="dc-search-result${focused}" role="option" id="dc-search-option-${current}" data-index="${current}">
				<span class="dc-search-result__meta">
					<span class="dc-search-result__title">${renderHighlighted(item.label || item.name || '')}</span>
					<span class="dc-search-result__sub">${escapeHtml(config.i18n?.searchHealthGoal || 'Health goal')}</span>
				</span>
			</a>
		`;
	});

	if (goalItems.length) {
		groups.push(`
			<div class="dc-search-group">
				<h3 class="dc-search-group__title">${escapeHtml(config.i18n?.searchHealthGoals || 'Health goals')}</h3>
				${goalItems.join('')}
			</div>
		`);
	}

	const adviceItems = (data.advice || []).map((item) => {
		const current = index++;
		const focused = current === focusedIndex ? ' is-focused' : '';
		return `
			<a href="${escapeHtml(item.url)}" class="dc-search-result${focused}" role="option" id="dc-search-option-${current}" data-index="${current}">
				<span class="dc-search-result__meta">
					<span class="dc-search-result__title">${renderHighlighted(item.label || item.title || '')}</span>
					<span class="dc-search-result__sub">${escapeHtml(config.i18n?.searchAdvice || 'Advice')}</span>
				</span>
			</a>
		`;
	});

	if (adviceItems.length) {
		groups.push(`
			<div class="dc-search-group">
				<h3 class="dc-search-group__title">${escapeHtml(config.i18n?.searchAdviceGroup || 'Advice')}</h3>
				${adviceItems.join('')}
			</div>
		`);
	}

	return { html: groups.filter(Boolean).join(''), total: index };
}

export function initSearch() {
	const panel = document.getElementById('dc-search-panel');
	const results = panel?.querySelector('[data-search-results]');
	const status = panel?.querySelector('[data-search-status]');
	const inputs = Array.from(document.querySelectorAll('[data-search-input]'));

	if (!panel || !results || !status || !inputs.length) {
		return;
	}

	const config = getConfig();
	let focusedIndex = -1;
	let resultLinks = [];
	let abortController = null;
	let isOpen = false;
	let activeInput = inputs[0];

	const syncInputs = (value, except) => {
		inputs.forEach((field) => {
			if (field !== except) {
				field.value = value;
			}
		});
	};

	const setExpanded = (expanded) => {
		inputs.forEach((field) => {
			field.setAttribute('aria-expanded', expanded ? 'true' : 'false');
		});
	};

	const highlight = () => {
		resultLinks.forEach((el, i) => {
			el.classList.toggle('is-focused', i === focusedIndex);
		});
		if (focusedIndex >= 0 && resultLinks[focusedIndex]) {
			activeInput.setAttribute('aria-activedescendant', resultLinks[focusedIndex].id);
			resultLinks[focusedIndex].scrollIntoView({ block: 'nearest' });
		} else {
			activeInput.removeAttribute('aria-activedescendant');
		}
	};

	const open = (input) => {
		activeInput = input;
		const wrap = input.closest('[data-search]') || input.parentElement;
		if (wrap && panel.parentElement !== wrap) {
			wrap.appendChild(panel);
		}
		panel.hidden = false;
		isOpen = true;
		setExpanded(true);
	};

	const close = () => {
		if (!isOpen) {
			return;
		}
		isOpen = false;
		panel.hidden = true;
		setExpanded(false);
		focusedIndex = -1;
		activeInput.removeAttribute('aria-activedescendant');
		if (abortController) {
			abortController.abort();
			abortController = null;
		}
	};

	const fetchResults = debounce(async (query) => {
		if (abortController) {
			abortController.abort();
		}
		abortController = new AbortController();

		if (query.length < MIN_QUERY_LENGTH) {
			status.textContent = '';
			results.innerHTML = renderSkeletons(3);
		}

		try {
			const url = new URL(`${config.restUrl}${SEARCH_ENDPOINT}`);
			if (query.length >= MIN_QUERY_LENGTH) {
				url.searchParams.set('q', query);
			}
			url.searchParams.set('limit', '10');

			if (query.length >= MIN_QUERY_LENGTH) {
				status.textContent = config.i18n?.searchLoading || 'Searching…';
				results.innerHTML = renderSkeletons(5);
			}

			const response = await fetch(url.toString(), {
				signal: abortController.signal,
				headers: { Accept: 'application/json' },
			});

			if (!response.ok) {
				throw new Error('Search failed');
			}

			const data = await response.json();

			if (query.length < MIN_QUERY_LENGTH) {
				results.innerHTML = renderSuggestions(data, config);
				status.textContent = '';
				resultLinks = [];
				focusedIndex = -1;
				highlight();
				return;
			}

			if (data.provider_available === false) {
				results.innerHTML = renderFallback(query, config);
				status.textContent = '';
				resultLinks = [];
				focusedIndex = -1;
				highlight();
				return;
			}

			const rendered = renderResults(data, focusedIndex, config);

			if (!rendered.total) {
				results.innerHTML = `<p class="dc-search-empty">${escapeHtml(config.i18n?.searchEmpty || 'No results found.')}</p>`;
				status.textContent = '';
				resultLinks = [];
				focusedIndex = -1;
				highlight();
				return;
			}

			results.innerHTML = rendered.html;
			resultLinks = Array.from(results.querySelectorAll('.dc-search-result'));
			status.textContent = `${rendered.total} results`;
			highlight();
		} catch (err) {
			if (err.name === 'AbortError') {
				return;
			}

			if (query.length >= MIN_QUERY_LENGTH) {
				results.innerHTML = renderFallback(query, config);
			} else {
				results.innerHTML = renderSuggestions({}, config);
			}
			status.textContent = '';
			resultLinks = [];
			focusedIndex = -1;
			highlight();
		}
	}, DEBOUNCE_MS);

	const runSearch = (query) => {
		fetchResults(query);
	};

	inputs.forEach((input) => {
		input.addEventListener('focus', () => {
			open(input);
			runSearch(input.value.trim());
		});

		input.addEventListener('input', () => {
			syncInputs(input.value, input);
			focusedIndex = -1;
			open(input);
			runSearch(input.value.trim());
		});

		input.addEventListener('keydown', (e) => {
			resultLinks = Array.from(results.querySelectorAll('.dc-search-result'));

			if (e.key === 'Escape') {
				e.preventDefault();
				close();
				input.blur();
				return;
			}

			if (e.key === 'ArrowDown') {
				e.preventDefault();
				if (!isOpen) {
					open(input);
					runSearch(input.value.trim());
				}
				focusedIndex = Math.min(focusedIndex + 1, Math.max(resultLinks.length - 1, 0));
				highlight();
				return;
			}

			if (e.key === 'ArrowUp') {
				e.preventDefault();
				focusedIndex = Math.max(focusedIndex - 1, 0);
				highlight();
				return;
			}

			if (e.key === 'Enter') {
				if (focusedIndex >= 0 && resultLinks[focusedIndex]) {
					e.preventDefault();
					resultLinks[focusedIndex].click();
					return;
				}

				const query = input.value.trim();
				if (query.length >= MIN_QUERY_LENGTH) {
					window.location.href = buildSearchFallbackUrl(query, config);
				}
			}
		});
	});

	document.addEventListener('pointerdown', (event) => {
		if (!isOpen || !(event.target instanceof Element)) {
			return;
		}
		if (event.target.closest('[data-search], .dc-search-panel')) {
			return;
		}
		close();
	});
}
